/** TicketDetallePage — Detalle D-B "Sidebar Drawer". Pixel-perfect del mockup. */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft, Send, Paperclip, Loader2, X, FileText, Download, ChevronRight,
  ChevronDown, ChevronUp,
  Mail, UserCheck, StickyNote, MessageSquareReply, Edit2,
  ClipboardList, ChevronsRight, ChevronsLeft, Folder, Shield,
  User as UserIcon, CheckCircle2, Inbox, Cog, Share2, Lock, Clock, CheckCheck, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { tienePermiso } from '../../utils/navPermisos';
import ClasificacionCascadeSelect from './components/ClasificacionCascadeSelect';
import { getSlaState, formatVencimiento } from './components/slaIndicator';
import { AuditTable } from './AuditoriaPage';
import LoadErrorCard from '../../components/LoadErrorCard';
import './tickets-theme.css';

const ESTADOS_INFO = {
  NUEVO:      { label: 'Nuevo',      icon: Inbox,         color: 'var(--tk-blue)',   bg: 'var(--tk-blue-soft)',   border: '#bfdbfe' },
  ASIGNADO:   { label: 'Asignado',   icon: UserCheck,     color: 'var(--tk-amber)',  bg: 'var(--tk-amber-soft)',  border: '#fde68a' },
  EN_PROCESO: { label: 'En proceso', icon: Cog,           color: 'var(--tk-violet)', bg: 'var(--tk-violet-soft)', border: '#d8c8f0' },
  TERMINADO:  { label: 'Terminado',  icon: CheckCircle2,  color: 'var(--tk-green)',  bg: 'var(--tk-green-soft)',  border: '#bbf7d0' },
};
const ESTADOS_KEYS = Object.keys(ESTADOS_INFO);

const fmtFecha = (s) => s ? new Date(s).toLocaleString('es-PE') : '—';
const initialsOf = (s) => {
  if (!s) return '?';
  const parts = s.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};
const colorForString = (s) => {
  if (!s) return '#94a3b8';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h}, 45%, 55%)`;
};

/** Caché a nivel de módulo (stale-while-revalidate): render instantáneo al reabrir
 *  un ticket ya visto. `tickets` keyed por ticketId; catálogos casi estáticos por sesión. */
const detalleCache = { catalogs: null, tickets: {} };

export default function TicketDetallePage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api, user } = useAuth();
  const [data, setData] = useState(() => detalleCache.tickets[ticketId] || null);
  const [responsables, setResponsables] = useState(() => detalleCache.catalogs?.responsables || []);
  const [prioridades, setPrioridades] = useState(() => detalleCache.catalogs?.prioridades || []);
  const [canales, setCanales] = useState(() => detalleCache.catalogs?.canales || []);
  const [tree, setTree] = useState(() => detalleCache.catalogs?.tree || []);
  const [loading, setLoading] = useState(() => !detalleCache.tickets[ticketId]);
  const [loadError, setLoadError] = useState(false);
  const [respondiendo, setRespondiendo] = useState(false);
  const [respFinalOpen, setRespFinalOpen] = useState(false);
  const [respFinalBusy, setRespFinalBusy] = useState(false);
  const [respBody, setRespBody] = useState('');
  const [adjuntosDraft, setAdjuntosDraft] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [notaTexto, setNotaTexto] = useState('');
  const [notaBody, setNotaBody] = useState('');
  const [notaAdjuntos, setNotaAdjuntos] = useState([]);
  const [notaUploading, setNotaUploading] = useState(false);
  const [notaBusy, setNotaBusy] = useState(false);
  const [giOpen, setGiOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('responder');
  const [reabrirOpen, setReabrirOpen] = useState(false);
  const [motivoReapertura, setMotivoReapertura] = useState('');
  const [reabriendo, setReabriendo] = useState(false);
  // Gestión Interna (Bloque B)
  const [responsablesInternos, setResponsablesInternos] = useState(() => detalleCache.catalogs?.responsablesInternos || []);
  const [prioridadesInternas, setPrioridadesInternas] = useState(() => detalleCache.catalogs?.prioridadesInternas || []);
  const [derivOpen, setDerivOpen] = useState(false);
  const [derivResp, setDerivResp] = useState('');
  const [derivPrioridad, setDerivPrioridad] = useState('');
  const [derivNota, setDerivNota] = useState('');
  const [derivando, setDerivando] = useState(false);
  const [gestionMsg, setGestionMsg] = useState({}); // { [gestionId]: texto }
  const [gestionBusy, setGestionBusy] = useState(null); // gestionId en proceso
  const [gestionAdjuntos, setGestionAdjuntos] = useState({}); // { [gestionId]: [adjuntos] }
  const [gestionUploading, setGestionUploading] = useState(null); // gestionId subiendo
  const [editandoClasificacion, setEditandoClasificacion] = useState(false);
  const [auditItems, setAuditItems] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return typeof window !== 'undefined' ? window.innerWidth > 900 : true; } catch { return true; }
  });
  const [openAcc, setOpenAcc] = useState({ detalles: true, sla: true, clasificacion: false, asignacion: false, adjuntos: false, auditoria: false });
  const [composerCollapsed, setComposerCollapsed] = useState(() => {
    try { return localStorage.getItem('tk-composer-collapsed') === '1'; }
    catch { return false; }
  });
  const [refreshing, setRefreshing] = useState(false);
  const [pendingNewMessages, setPendingNewMessages] = useState(0); // mensajes ENTRANTES nuevos por polling
  const timelineWrapRef = useRef(null);
  const endOfTimelineRef = useRef(null);
  const initialScrollDoneRef = useRef(false);
  const prevIncomingIdsRef = useRef(null); // Set de IDs entrantes ya conocidos
  const lastFingerprintRef = useRef(null); // Huella del contenido para evitar re-render innecesario
  // Regla de negocio: si el área del usuario no puede responder al cliente
  // (y no es ADMINISTRADOR/SUPERADMIN), el composer del cliente queda minimizado
  // y no expandible. El backend expone user.puede_responder_cliente.
  // Permiso de edición (matriz RBAC ca_tickets.editar). Si no lo tiene, la vista
  // de detalle queda en modo solo-lectura (sin responder, notas ni reasignaciones).
  const puedeEditar = tienePermiso(user, 'ca_tickets', 'editar');
  const bloqueoCliente = user?.puede_responder_cliente === false || !puedeEditar;
  const toggleComposer = () => {
    if (bloqueoCliente) return;
    setComposerCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('tk-composer-collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);

  // Forzar composer minimizado si el área del usuario no puede responder al cliente.
  useEffect(() => {
    if (bloqueoCliente) setComposerCollapsed(true);
  }, [bloqueoCliente]);
  const notaFileInputRef = useRef(null);

  const fetchTicket = async ({ silent = false, force = false, signal = null } = {}) => {
    // El `signal` lo provee el effect del montaje actual. Si ya fue abortado
    // (navegación rápida / doble montaje de StrictMode), no arrancamos nada.
    const sig = signal || abortRef.current?.signal;
    if (sig?.aborted) return;
    if (silent) setRefreshing(true);
    else { setLoading(true); setLoadError(false); }
    // El auto-refresh silencioso NO debe molestar con toasts si expira: se reintenta
    // en el siguiente ciclo. Timeout más holgado para hilos grandes (25s) y sin toast.
    const cfg = { timeout: silent ? 25000 : 20000, signal: sig, suppressErrorToast: silent };
    try {
      const needCatalogs = !detalleCache.catalogs;
      const reqs = [api.get(`/tickets/${ticketId}`, cfg)];
      if (needCatalogs) {
        reqs.push(
          api.get('/tickets/responsables', cfg),
          api.get('/tickets/prioridades', cfg),
          api.get('/tickets/canales', cfg),
          api.get('/tickets/clasificacion/tree', cfg),
          api.get('/tickets/responsables-internos', cfg).catch(() => ({ data: [] })),
          api.get('/tickets/prioridades-internas', cfg).catch(() => ({ data: [] })),
        );
      }
      const results = await Promise.all(reqs);
      // La petición pudo ser abortada mientras esperábamos (desmontaje / cambio de
      // ticket). Si es así, esta instancia ya no manda: no tocamos el estado.
      if (sig?.aborted) return;
      const fresh = results[0].data;
      // Detectar mensajes ENTRANTES nuevos (usa el ref; null en 1ª carga => sin toast).
      const newIncoming = (fresh?.mensajes || []).filter(
        (m) => m.direccion === 'ENTRANTE' && !prevIncomingIdsRef.current?.has(m.id)
      );
      if (prevIncomingIdsRef.current !== null && newIncoming.length > 0) {
        setPendingNewMessages((n) => n + newIncoming.length);
        toast.message('Nuevo mensaje recibido', { duration: 4000 });
      }
      prevIncomingIdsRef.current = new Set(
        (fresh?.mensajes || []).filter((m) => m.direccion === 'ENTRANTE').map((m) => m.id)
      );
      // Huella barata del contenido: si nada cambió en un refresh silencioso, NO
      // reemplazamos el estado, evitando recargar iframes/imágenes (parpadeo).
      const msgs = fresh?.mensajes || [];
      const gs = fresh?.gestiones || [];
      const fingerprint = JSON.stringify({
        n: msgs.length,
        lastMsg: msgs[msgs.length - 1]?.id,
        lastMsgLen: msgs[msgs.length - 1]?.cuerpo_html?.length,
        estado: fresh?.ticket?.estado,
        responsable: fresh?.ticket?.responsable_id,
        g: gs.length,
        lastG: gs[gs.length - 1]?.id,
      });
      if (silent && !force && lastFingerprintRef.current === fingerprint) {
        return; // sin cambios reales → no re-render (solo auto-poll pasivo)
      }
      lastFingerprintRef.current = fingerprint;
      setData(fresh);
      detalleCache.tickets[ticketId] = fresh;
      if (needCatalogs) {
        const [, rRes, pRes, cRes, treeRes, riRes, piRes] = results;
        const cat = {
          responsables: rRes.data || [],
          prioridades: pRes.data || [],
          canales: cRes.data || [],
          tree: treeRes.data || [],
          responsablesInternos: riRes.data || [],
          prioridadesInternas: (piRes.data || []).filter((p) => p.activo !== false),
        };
        setResponsables(cat.responsables);
        setPrioridades(cat.prioridades);
        setCanales(cat.canales);
        setTree(cat.tree);
        setResponsablesInternos(cat.responsablesInternos);
        setPrioridadesInternas(cat.prioridadesInternas);
        detalleCache.catalogs = cat;
      }
    } catch (e) {
      // Cancelaciones (navegación / pestaña en 2º plano): ignorar.
      const cancelado = e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';
      if (cancelado) return;
      if (!silent) {
        const status = e?.response?.status;
        if (status === 404) {
          // El ticket no existe: volver al listado con aviso.
          toast.error(e?.response?.data?.detail || 'Ticket no encontrado');
          navigate('/admin/centro-ayuda/tickets');
        } else if (!data) {
          // Timeout (ECONNABORTED), 5xx o conexión: si no hay datos que mostrar,
          // presentar tarjeta de reintento en vez de dejar el spinner infinito.
          setLoadError(true);
        }
      }
    } finally {
      // Solo la petición vigente (no abortada) resuelve el loader. Una petición
      // abortada por un montaje anterior NO debe apagar/encender el spinner del
      // montaje real (raíz del cuelgue en StrictMode + navegación en ráfaga).
      if (!sig?.aborted) {
        if (silent) setRefreshing(false);
        else setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Reset de UI al cambiar de ticket (evita fuga de gestiones/pestaña entre tickets)
    initialScrollDoneRef.current = false;
    // Controlador de aborto POR MONTAJE: se aborta solo en el cleanup de este effect
    // (cambio de ticket / desmontaje). No se aborta al iniciar cada fetch, para no
    // sabotear la carga en el doble-montaje de StrictMode.
    const controller = new AbortController();
    abortRef.current = controller;
    setPendingNewMessages(0);
    setActiveTab('responder');
    setEditandoClasificacion(false);
    setDerivOpen(false); setDerivResp(''); setDerivPrioridad(''); setDerivNota('');
    setGestionMsg({});
    setGestionAdjuntos({}); setGestionUploading(null);
    // stale-while-revalidate: render instantáneo desde caché + refresco silencioso.
    const cached = detalleCache.tickets[ticketId];
    if (cached) {
      setData(cached);
      setLoading(false);
      prevIncomingIdsRef.current = new Set(
        (cached.mensajes || []).filter((m) => m.direccion === 'ENTRANTE').map((m) => m.id)
      );
    } else {
      setData(null);
      setLoading(true);
      prevIncomingIdsRef.current = null;
    }
    fetchTicket({ silent: !!cached, signal: controller.signal });
    // Al cambiar de ticket o desmontar, cancela SOLO las peticiones de este montaje
    // (evita saturar el pool de conexiones y sabotear el montaje real de StrictMode).
    return () => { try { controller.abort(); } catch { /* noop */ } };
    /* eslint-disable-next-line */
  }, [ticketId]);

  // Mantener la caché sincronizada con updates locales (respuestas, reapertura, etc.).
  useEffect(() => {
    if (data && ticketId) detalleCache.tickets[ticketId] = data;
    /* eslint-disable-next-line */
  }, [data]);

  // Deep-link: abrir la pestaña Gestión Interna al llegar con ?tab=nota (desde correo de alerta).
  useEffect(() => {
    if (data?.ticket && searchParams.get('tab') === 'nota') {
      setGiOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
    /* eslint-disable-next-line */
  }, [data, searchParams]);

  // Al llegar desde el Kanban con intento de "Terminar": guiar al cierre por respuesta final.
  useEffect(() => {
    if (data?.ticket && searchParams.get('cerrar') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('cerrar');
      setSearchParams(next, { replace: true });
      iniciarCierre();
    }
    /* eslint-disable-next-line */
  }, [data, searchParams]);

  // Al llegar desde el Kanban con ?msg=<id>: hacer scroll y resaltar ese mensaje.
  useEffect(() => {
    const msgId = searchParams.get('msg');
    if (!data?.ticket || !msgId) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`msg-${msgId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('tk-msg-highlight');
        setTimeout(() => el.classList.remove('tk-msg-highlight'), 2800);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('msg');
      setSearchParams(next, { replace: true });
    }, 400);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [data, searchParams]);


  // Auto-refresh cada 30s mientras la pestaña esté activa.
  useEffect(() => {
    if (!ticketId) return undefined;
    const POLL_MS = 30_000;
    let timer = null;
    const tryRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      fetchTicket({ silent: true });
    };
    const start = () => { if (!timer) timer = setInterval(tryRefresh, POLL_MS); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.visibilityState === 'visible') start(); else stop(); };
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line
  }, [ticketId]);

  // Auto-scroll al último mensaje al cargar inicialmente.
  useEffect(() => {
    if (loading || !data || initialScrollDoneRef.current) return;
    // Esperar a que el DOM pinte
    const id = setTimeout(() => {
      const wrap = timelineWrapRef.current;
      if (wrap) {
        wrap.scrollTop = wrap.scrollHeight;
        initialScrollDoneRef.current = true;
      }
    }, 50);
    return () => clearTimeout(id);
  }, [loading, data]);

  const scrollToLatest = () => {
    const wrap = timelineWrapRef.current;
    if (wrap) wrap.scrollTo({ top: wrap.scrollHeight, behavior: 'smooth' });
    setPendingNewMessages(0);
  };

  const patchTicket = async (changes) => {
    try {
      const { data: updated } = await api.patch(`/tickets/${ticketId}`, changes);
      setData((prev) => ({ ...prev, ticket: updated }));
      toast.success('Cambios guardados');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    }
  };

  const cambiarEstado = (v) => {
    if (v === 'TERMINADO') { iniciarCierre(); return; }
    if (data?.ticket?.estado === 'TERMINADO' && v === 'EN_PROCESO') { setMotivoReapertura(''); setReabrirOpen(true); return; }
    patchTicket({ estado: v });
  };

  const confirmarReapertura = async () => {
    const motivo = motivoReapertura.trim();
    if (!motivo) { toast.error('Indique el motivo de la reapertura'); return; }
    setReabriendo(true);
    try {
      const { data: updated } = await api.patch(`/tickets/${ticketId}`, { estado: 'EN_PROCESO', motivo_reapertura: motivo });
      setData((prev) => ({ ...prev, ticket: updated }));
      toast.success('Ticket reabierto');
      setReabrirOpen(false); setMotivoReapertura('');
      await fetchTicket({ silent: true, force: true });
      scrollToLatest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reabrir el ticket');
    } finally { setReabriendo(false); }
  };

  const guardarClasificacion = async (newId) => {
    try {
      const { data: updated } = await api.patch(`/tickets/${ticketId}`, { clasificacion_id: newId });
      setData((prev) => ({ ...prev, ticket: updated }));
      setEditandoClasificacion(false);
      toast.success(newId ? 'Clasificación actualizada' : 'Clasificación removida');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando clasificación');
    }
  };

  const subirAdjunto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (files.length === 0) return;
    const grandes = files.filter((f) => f.size > 20 * 1024 * 1024);
    const validos = files.filter((f) => f.size <= 20 * 1024 * 1024);
    if (grandes.length) toast.error(`Omitido(s) por superar 20 MB: ${grandes.map((f) => f.name).join(', ')}`);
    if (validos.length === 0) return;
    setUploading(true);
    try {
      const results = await Promise.allSettled(validos.map((file) => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/tickets/${ticketId}/adjuntos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }));
      const ok = results.filter((r) => r.status === 'fulfilled').map((r) => r.value.data);
      const fallidos = results.length - ok.length;
      if (ok.length) setAdjuntosDraft((prev) => [...prev, ...ok]);
      if (ok.length) toast.success(`${ok.length} archivo(s) cargado(s)`);
      if (fallidos) toast.error(`${fallidos} archivo(s) no se pudieron cargar`);
    } finally {
      setUploading(false);
    }
  };

  const subirAdjuntoNota = async (e) => {
    const files = Array.from(e.target.files || []);
    if (notaFileInputRef.current) notaFileInputRef.current.value = '';
    if (files.length === 0) return;
    const validos = files.filter((f) => f.size <= 20 * 1024 * 1024);
    if (files.length !== validos.length) toast.error('Se omitieron archivos que superan 20 MB');
    if (validos.length === 0) return;
    setNotaUploading(true);
    try {
      const results = await Promise.allSettled(validos.map((file) => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/tickets/${ticketId}/adjuntos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }));
      const ok = results.filter((r) => r.status === 'fulfilled').map((r) => r.value.data);
      if (ok.length) { setNotaAdjuntos((prev) => [...prev, ...ok]); toast.success(`${ok.length} archivo(s) cargado(s)`); }
      if (results.length - ok.length) toast.error(`${results.length - ok.length} archivo(s) no se pudieron cargar`);
    } finally { setNotaUploading(false); }
  };

  const responder = async () => {
    if (!data?.ticket?.responsable_id) {
      toast.error('Asigna un responsable principal antes de responder al cliente.'); return;
    }
    if (data?.ticket?.estado === 'TERMINADO') {
      toast.info('El ticket está terminado. Reábralo para responder al cliente.'); return;
    }
    if (!respBody || respBody.trim() === '' || respBody === '<p><br></p>') {
      toast.error('Escriba un mensaje antes de enviar'); return;
    }
    setRespondiendo(true);
    try {
      const { data: result } = await api.post(`/tickets/${ticketId}/responder`, {
        cuerpo_html: respBody,
        adjuntos_ids: adjuntosDraft.map((a) => a.id),
      });
      if (result.ok) {
        toast.success('Respuesta enviada al cliente');
        setRespBody(''); setAdjuntosDraft([]);
        await fetchTicket({ silent: true, force: true });
        scrollToLatest();
      } else {
        toast.error(`Error: ${result.error || 'desconocido'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando respuesta');
    } finally { setRespondiendo(false); }
  };

  const iniciarCierre = () => {
    if (data?.ticket?.estado === 'TERMINADO') return;
    setActiveTab('responder');
    toast.info("Escribe la respuesta final y presiona 'Responder y Terminar' para cerrar el ticket.");
    setTimeout(() => { try { document.querySelector('.ql-editor')?.focus(); } catch (_) {} }, 300);
  };

  const enviarRespuestaFinal = async () => {
    if (!respBody || respBody.trim() === '' || respBody === '<p><br></p>') {
      toast.error('Escriba la respuesta final antes de cerrar'); return;
    }
    setRespFinalBusy(true);
    try {
      const { data: result } = await api.post(`/tickets/${ticketId}/respuesta-final`, {
        cuerpo_html: respBody,
        adjuntos_ids: adjuntosDraft.map((a) => a.id),
      });
      if (result.ok) {
        toast.success('Respuesta final enviada. Ticket cerrado.');
        setRespBody(''); setAdjuntosDraft([]); setRespFinalOpen(false);
        await fetchTicket({ silent: true, force: true });
        scrollToLatest();
      }
    } catch (e) {
      setRespFinalOpen(false);
      toast.error(e?.response?.data?.detail || 'No se pudo cerrar el ticket');
    } finally { setRespFinalBusy(false); }
  };

  const guardarNota = async () => {
    if (data?.ticket?.estado === 'TERMINADO') { toast.error('El ticket está terminado. Reábralo para registrar notas.'); return; }
    const vacio = !notaBody || notaBody.trim() === '' || notaBody === '<p><br></p>';
    if (vacio && notaAdjuntos.length === 0) { toast.error('Escriba una nota o adjunte un archivo'); return; }
    setNotaBusy(true);
    try {
      await api.post(`/tickets/${ticketId}/nota`, {
        cuerpo_html: vacio ? '' : notaBody,
        cuerpo_texto: (notaBody || '').replace(/<[^>]+>/g, ' ').trim(),
        adjuntos_ids: notaAdjuntos.map((a) => a.id),
      });
      toast.success('Nota interna guardada');
      setNotaBody(''); setNotaTexto(''); setNotaAdjuntos([]);
      await fetchTicket({ silent: true, force: true });
      scrollToLatest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando nota');
    } finally { setNotaBusy(false); }
  };
  void guardarNota;

  const derivarGestion = async () => {
    if (!data?.ticket?.responsable_id) {
      toast.error('Asigna un responsable principal antes de derivar a gestión interna.'); return;
    }
    if (data?.ticket?.estado === 'TERMINADO') {
      toast.info('El ticket está terminado. Reábralo para derivar a gestión interna.'); return;
    }
    if (!derivResp) { toast.error('Selecciona un responsable interno'); return; }
    setDerivando(true);
    try {
      await api.post(`/tickets/${ticketId}/gestion/derivar`, {
        responsable_interno_id: derivResp,
        prioridad_interna_id: derivPrioridad || null,
        mensaje: derivNota.trim() || null,
      });
      toast.success('Gestión interna derivada');
      setDerivResp(''); setDerivPrioridad(''); setDerivNota(''); setDerivOpen(false);
      await fetchTicket({ silent: true, force: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al derivar');
    } finally { setDerivando(false); }
  };

  const responderGestion = async (gestionId) => {
    const texto = (gestionMsg[gestionId] || '').trim();
    const adjuntos = gestionAdjuntos[gestionId] || [];
    if (!texto && adjuntos.length === 0) return;
    setGestionBusy(gestionId);
    try {
      await api.post(`/tickets/${ticketId}/gestion/${gestionId}/mensaje`, {
        cuerpo_texto: texto || null,
        adjuntos_ids: adjuntos.map((a) => a.id),
      });
      setGestionMsg((p) => ({ ...p, [gestionId]: '' }));
      setGestionAdjuntos((p) => ({ ...p, [gestionId]: [] }));
      await fetchTicket({ silent: true, force: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar mensaje');
    } finally { setGestionBusy(null); }
  };

  const subirAdjuntoGestion = async (gestionId, e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    if (files.length === 0) return;
    const grandes = files.filter((f) => f.size > 20 * 1024 * 1024);
    const validos = files.filter((f) => f.size <= 20 * 1024 * 1024);
    if (grandes.length) toast.error(`Omitido(s) por superar 20 MB: ${grandes.map((f) => f.name).join(', ')}`);
    if (validos.length === 0) return;
    setGestionUploading(gestionId);
    try {
      const results = await Promise.allSettled(validos.map((file) => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/tickets/${ticketId}/adjuntos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }));
      const ok = results.filter((r) => r.status === 'fulfilled').map((r) => r.value.data);
      const fallidos = results.length - ok.length;
      if (ok.length) setGestionAdjuntos((p) => ({ ...p, [gestionId]: [...(p[gestionId] || []), ...ok] }));
      if (ok.length) toast.success(`${ok.length} archivo(s) cargado(s)`);
      if (fallidos) toast.error(`${fallidos} archivo(s) no se pudieron cargar`);
    } finally { setGestionUploading(null); }
  };

  const quitarAdjuntoGestion = (gestionId, adjId) => {
    setGestionAdjuntos((p) => ({ ...p, [gestionId]: (p[gestionId] || []).filter((a) => a.id !== adjId) }));
  };

  const cerrarGestion = async (gestionId) => {
    setGestionBusy(gestionId);
    try {
      await api.post(`/tickets/${ticketId}/gestion/${gestionId}/cerrar`);
      toast.success('Gestión interna cerrada');
      await fetchTicket({ silent: true, force: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al cerrar la gestión');
    } finally { setGestionBusy(null); }
  };

  // Unifica "Enviar" + "Cerrar gestión": registra el mensaje y luego cierra la gestión.
  const responderYCerrarGestion = async (gestionId) => {
    const texto = (gestionMsg[gestionId] || '').trim();
    if (!texto) { toast.error('Escribe una respuesta antes de cerrar la gestión'); return; }
    const adjuntos = gestionAdjuntos[gestionId] || [];
    setGestionBusy(gestionId);
    try {
      await api.post(`/tickets/${ticketId}/gestion/${gestionId}/mensaje`, {
        cuerpo_texto: texto,
        adjuntos_ids: adjuntos.map((a) => a.id),
        notificar: false,
      });
      setGestionMsg((p) => ({ ...p, [gestionId]: '' }));
      setGestionAdjuntos((p) => ({ ...p, [gestionId]: [] }));
      await api.post(`/tickets/${ticketId}/gestion/${gestionId}/cerrar`);
      toast.success('Gestión respondida y cerrada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al responder y cerrar la gestión');
    } finally {
      // Reconcilia la UI con el estado real del servidor aunque haya timeout.
      try { await fetchTicket({ silent: true, force: true }); } catch { /* noop */ }
      setGestionBusy(null);
    }
  };

  const verAdjunto = async (adj) => {
    try {
      const { data: r } = await api.get(`/tickets/${ticketId}/adjuntos/${adj.id}/url`);
      window.open(r.url, '_blank', 'noopener');
    } catch { toast.error('No se pudo abrir el adjunto'); }
  };

  const cargarAuditoria = () => {
    if (auditItems !== null) return;
    setAuditLoading(true);
    api.get(`/tickets/${ticketId}/audit`)
      .then((r) => setAuditItems(r.data || []))
      .catch(() => setAuditItems([]))
      .finally(() => setAuditLoading(false));
  };

  if (loadError && !data) {
    return (
      <div className="tickets-scope">
        <LoadErrorCard
          onRetry={() => fetchTicket({ force: true })}
          retrying={loading}
          title="No se pudo cargar el ticket"
          message="La solicitud no respondió a tiempo o el servidor está ocupado. Inténtalo de nuevo."
          testId="ticket-detalle-load-error"
        />
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="tickets-scope" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'var(--tk-shell-content-h)' }}>
        <Loader2 style={{ width: 32, height: 32, color: 'var(--tk-text-soft)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const t = data.ticket;
  const gestiones = data.gestiones || [];
  const gestionesAbiertas = gestiones.filter((g) => g.estado_interno === 'ABIERTO');
  const hayGestionAbierta = gestionesAbiertas.length > 0;
  const puedeDerivar = puedeEditar && (['ADMINISTRADOR', 'SUPERADMIN', 'SUPERVISOR'].includes(user?.rol) || t.responsable_id === user?.id);
  const gestionTabActive = giOpen;
  // Read-only del sidebar: con la pestaña Gestión Interna activa O mientras existan gestiones abiertas (permanente).
  const sidebarReadOnly = gestionTabActive || hayGestionAbierta || !puedeEditar;
  const estadoLockTooltip = hayGestionAbierta
    ? 'Para cambiar de estado debe cerrar las gestiones internas abiertas'
    : (gestionTabActive ? 'Edición pausada mientras gestionas internamente' : undefined);
  const configLockTooltip = hayGestionAbierta
    ? 'Para editar el ticket debe cerrar las gestiones internas abiertas'
    : (gestionTabActive ? 'Edición pausada mientras gestionas internamente' : undefined);

  // Gating "Responder y Terminar" (mismas reglas que TERMINAR)
  const faltantesCierre = [];
  if (!t.responsable_id) faltantesCierre.push('responsable');
  if (!t.prioridad_id) faltantesCierre.push('prioridad');
  if ((t.clasificacion_path_ids || []).length < 2) faltantesCierre.push('clasificación (mínimo nivel 2)');
  if (hayGestionAbierta) faltantesCierre.push('cerrar gestiones internas abiertas');
  const puedeVerRespuestaFinal = puedeDerivar && t.estado !== 'TERMINADO';
  const esTerminado = t.estado === 'TERMINADO';
  const puedeCerrarUX = faltantesCierre.length === 0;
  const bodyVacio = !respBody || respBody.replace(/<(p|br|\/p|\/br|div|\/div)\s*\/?>/gi, '').replace(/&nbsp;/gi, '').trim() === '';
  const respFinalTooltip = !puedeCerrarUX ? `Para cerrar falta: ${faltantesCierre.join(', ')}` : 'Enviar respuesta y cerrar el ticket';
  const mensajes = (data.mensajes || []).filter((m) => !(m.direccion === 'SISTEMA' && m.autor_tipo === 'SISTEMA' && m.asunto === '(borrador adjunto)'));
  const pri = prioridades.find((p) => p.id === t.prioridad_id);
  const canal = canales.find((c) => c.id === t.canal_id);
  const resp = responsables.find((r) => r.id === t.responsable_id);
  const respNombre = resp?.nombre || t.responsable_nombre || null;
  const sinResponsable = !t.responsable_id;
  // Incluir al responsable actual en el selector aunque quede fuera del filtro (área sin permiso).
  const responsablesConActual = (t.responsable_id && !resp)
    ? [...responsables, { id: t.responsable_id, nombre: respNombre || 'Responsable actual', correo: '' }]
    : responsables;
  const sla = getSlaState(t);
  const estadoInfo = ESTADOS_INFO[t.estado] || ESTADOS_INFO.NUEVO;
  const adjuntosTicket = [];
  for (const m of mensajes) for (const a of (m.adjuntos || [])) if (!a.es_inline) adjuntosTicket.push(a);

  const slaToneClass =
    sla?.key === 'VENCIDO' ? 'late' :
    sla?.key === 'WARN' ? 'warn' :
    sla?.key === 'OK' ? 'ok' :
    sla?.key === 'CUMPLIDO' ? 'cumplido' :
    sla?.key === 'INCUMPLIDO' ? 'incumplido' : '';

  return (
    <div className="tickets-scope tk-shell tk-shell-flush" style={{ background: 'var(--tk-bg)' }}>
      {/* HEADER */}
      <div className={`tk-detalle-header ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
        <div className="tk-h-left">
          <button className="tk-back-btn" onClick={() => navigate('/admin/centro-ayuda/tickets')} data-testid="btn-volver-kanban" title="Volver al Kanban">
            <ArrowLeft style={{ width: 16, height: 16 }} />
          </button>

          <div className="tk-h-info">
            <div className="tk-h-meta">
              <b>{t.codigo}</b>
              <span>·</span>
              <Mail style={{ width: 12, height: 12 }} />
              <span>{canal?.nombre || 'Correo'}</span>
              {t.clasificacion_ruta && (
                <>
                  <span>·</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.clasificacion_ruta}>{t.clasificacion_ruta}</span>
                </>
              )}
            </div>
            <h1 className="tk-h-asunto">{t.asunto_original || '(sin asunto)'}</h1>
          </div>

          <button
            type="button"
            className={`tk-gi-toggle ${giOpen ? 'active' : ''}`}
            onClick={() => setGiOpen((v) => !v)}
            data-testid="btn-toggle-gestion-interna"
            title={giOpen ? 'Ocultar Gestión Interna' : 'Mostrar Gestión Interna'}
          >
            <Share2 style={{ width: 14, height: 14 }} />
            Gestión Interna
            {gestionesAbiertas.length > 0 && (
              <span className="tk-gi-tab-badge" data-testid="gestion-abiertas-badge">{gestionesAbiertas.length}</span>
            )}
            {giOpen ? <X style={{ width: 13, height: 13, marginLeft: 2 }} /> : <ChevronDown style={{ width: 13, height: 13, marginLeft: 2 }} />}
          </button>
        </div>

        <div className="tk-h-right">
          {sla && (
            <span
              className={`tk-sla-rich ${slaToneClass}`}
              title={t.fecha_vencimiento_sla ? `Vence: ${formatVencimiento(t.fecha_vencimiento_sla)}` : sla.label}
              data-testid="sla-badge"
            >
              <span className="tk-dot" />
              {sla.detalle || sla.label}
            </span>
          )}
          <Select value={t.estado} onValueChange={cambiarEstado} disabled={hayGestionAbierta || gestionTabActive}>
            <SelectTrigger
              className="h-auto p-0 border-0 focus:ring-0"
              data-testid="select-estado"
              title={estadoLockTooltip}
              style={{
                background: estadoInfo.bg,
                color: estadoInfo.color,
                border: `1px solid ${estadoInfo.border}`,
                padding: '6px 14px',
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
              }}
            >
              <SelectValue>{estadoInfo.label.toUpperCase()}</SelectValue>
            </SelectTrigger>
            <SelectContent>{ESTADOS_KEYS.map((k) => <SelectItem key={k} value={k}>{ESTADOS_INFO[k].label}</SelectItem>)}</SelectContent>
          </Select>
          <button className="tk-toggle-btn" onClick={() => setSidebarOpen((s) => !s)} title={sidebarOpen ? 'Ocultar panel' : 'Mostrar panel'} data-testid="btn-toggle-sidebar">
            {sidebarOpen ? <ChevronsRight style={{ width: 16, height: 16 }} /> : <ChevronsLeft style={{ width: 16, height: 16 }} />}
          </button>
        </div>
      </div>

      {/* LAYOUT */}
      <div className={`tk-detalle-layout ${sidebarOpen ? '' : 'collapsed'}`}>
        {/* MAIN COL */}
        <div className="tk-main-col">
          <div className="tk-timeline-wrap" data-testid="hilo-mensajes" ref={timelineWrapRef}>
            {mensajes.length === 0 ? (
              <p style={{ fontSize: 14, color: 'var(--tk-text-soft)', fontStyle: 'italic', textAlign: 'center', padding: '40px 0' }}>Sin mensajes en este ticket.</p>
            ) : (
              <div className="tk-timeline">
                {mensajes.map((m) => (
                  <div key={m.id} id={`msg-${m.id}`} data-testid={`msg-anchor-${m.id}`}>
                    <TimelineMessage mensaje={m} verAdjunto={verAdjunto} />
                  </div>
                ))}
              </div>
            )}
            {pendingNewMessages > 0 && (
              <button
                type="button"
                className="tk-newmsg-floater"
                onClick={scrollToLatest}
                data-testid="btn-nuevo-mensaje-floater"
                aria-label="Ir al mensaje más reciente"
              >
                <ChevronDown style={{ width: 13, height: 13 }} />
                {pendingNewMessages === 1 ? '1 nuevo mensaje' : `${pendingNewMessages} nuevos mensajes`}
              </button>
            )}
          </div>

          <div className={`tk-composer ${(composerCollapsed || bloqueoCliente) ? 'is-collapsed' : ''}`}>
            <button
              type="button"
              className="tk-composer-toggle"
              onClick={toggleComposer}
              disabled={bloqueoCliente}
              aria-label={composerCollapsed ? "Expandir composer" : "Minimizar composer"}
              title={bloqueoCliente ? "Tu área no puede responder al cliente. Usa Gestión Interna." : (composerCollapsed ? "Expandir composer" : "Minimizar composer")}
              data-testid="btn-composer-toggle"
              style={bloqueoCliente ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              {composerCollapsed ? <ChevronUp /> : <ChevronDown />}
            </button>

            {bloqueoCliente ? (
              <div className="tk-composer-mini is-locked" data-testid="composer-mini-locked" style={{ cursor: 'not-allowed' }}>
                <span className="tk-composer-mini-tab">
                  <Lock style={{ width: 12, height: 12, marginRight: 6 }} /> Responder al cliente bloqueado
                </span>
                <span className="tk-composer-mini-placeholder">
                  Tu área no tiene permiso para responder al cliente. Interactúa mediante Gestión Interna.
                </span>
              </div>
            ) : composerCollapsed ? (
              <div className="tk-composer-mini" onClick={toggleComposer} data-testid="composer-mini" role="button" tabIndex={0}>
                <span className="tk-composer-mini-tab">
                  {activeTab === 'responder' ? 'Responder al cliente' : activeTab === 'notas' ? 'Notas' : 'Ver auditoría'}
                </span>
                <span className="tk-composer-mini-placeholder">
                  {activeTab === 'responder' ? 'Click para escribir respuesta…' : activeTab === 'notas' ? 'Click para registrar una nota interna…' : 'Click para ver la auditoría…'}
                </span>
              </div>
            ) : (
              <>
            <div className="tk-ctabs">
              <button className={`tk-ctab ${activeTab === 'responder' ? 'active' : ''}`} onClick={() => setActiveTab('responder')} data-testid="tab-responder">
                <MessageSquareReply style={{ width: 13, height: 13 }} /> Responder al cliente
              </button>
              <button className={`tk-ctab ${activeTab === 'notas' ? 'active' : ''}`} onClick={() => setActiveTab('notas')} data-testid="tab-notas">
                <StickyNote style={{ width: 13, height: 13 }} /> Notas
              </button>
              <button
                className={`tk-ctab ${activeTab === 'auditoria' ? 'active' : ''}`}
                onClick={() => { setActiveTab('auditoria'); cargarAuditoria(); }}
                data-testid="tab-auditoria"
              >
                <ClipboardList style={{ width: 13, height: 13 }} /> Ver auditoría
              </button>
            </div>

            {activeTab === 'responder' && (
              <div>
                {sinResponsable && (
                  <div className="tk-responder-cerrado" data-testid="responder-sin-responsable-hint">
                    <Lock style={{ width: 14, height: 14, flexShrink: 0 }} />
                    <span>Asigna un <strong>responsable principal</strong> al ticket para poder responder al cliente.</span>
                  </div>
                )}
                {esTerminado && (
                  <div className="tk-responder-cerrado" data-testid="responder-terminado-hint">
                    <CheckCircle2 style={{ width: 14, height: 14, flexShrink: 0 }} />
                    <span>Este ticket está <strong>terminado</strong>. Para volver a interactuar con el cliente, reábralo cambiando el estado a <strong>«En proceso»</strong>.</span>
                  </div>
                )}
                <div className="tk-editor-wrap">
                  <ReactQuill
                    value={respBody}
                    onChange={setRespBody}
                    readOnly={esTerminado || sinResponsable}
                    modules={{ toolbar: [[{ header: [false, 2, 3] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                    placeholder={sinResponsable ? 'Asigna un responsable principal para responder…' : esTerminado ? 'El ticket está terminado. Reábralo para responder…' : 'Escriba su respuesta al cliente…'}
                  />
                </div>
                {adjuntosDraft.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {adjuntosDraft.map((a) => (
                      <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 8px', background: 'var(--tk-slate-100)', borderRadius: 4, fontSize: 11 }}>
                        <FileText style={{ width: 11, height: 11 }} />
                        {a.nombre}
                        <button onClick={() => setAdjuntosDraft((p) => p.filter((x) => x.id !== a.id))} style={{ background: 'transparent', border: 0, cursor: 'pointer' }}>
                          <X style={{ width: 11, height: 11, color: 'var(--tk-rose)' }} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="tk-composer-row">
                  <div className="tk-ed-actions">
                    <input ref={fileInputRef} type="file" multiple onChange={subirAdjunto} style={{ display: 'none' }} data-testid="input-adjunto" />
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading || esTerminado || sinResponsable} data-testid="btn-adjuntar">
                      {uploading ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Paperclip style={{ width: 12, height: 12 }} />}
                      Adjuntar
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button className="tk-btn-primary-lg" onClick={responder} disabled={respondiendo || bodyVacio || esTerminado || sinResponsable} title={sinResponsable ? 'Asigna un responsable principal para responder.' : esTerminado ? 'El ticket está terminado. Reábralo para responder.' : undefined} data-testid="btn-enviar-respuesta">
                      {respondiendo ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 13, height: 13 }} />}
                      Enviar
                    </button>
                    {puedeVerRespuestaFinal && (
                      <button
                        className="tk-btn-final"
                        onClick={() => setRespFinalOpen(true)}
                        disabled={respondiendo || respFinalBusy || bodyVacio || !puedeCerrarUX || sinResponsable}
                        title={respFinalTooltip}
                        data-testid="btn-respuesta-final"
                      >
                        <CheckCheck style={{ width: 13, height: 13 }} />
                        Responder y Terminar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notas' && (
              <div>
                {esTerminado ? (
                  <div className="tk-responder-cerrado" data-testid="notas-terminado-hint">
                    <CheckCircle2 style={{ width: 14, height: 14, flexShrink: 0 }} />
                    <span>Este ticket está <strong>terminado</strong>. Para registrar notas internas, reábralo cambiando el estado a <strong>«En proceso»</strong>.</span>
                  </div>
                ) : (
                  <div className="tk-nota-hint" data-testid="notas-hint">
                    <StickyNote style={{ width: 13, height: 13, flexShrink: 0 }} />
                    <span>Nota interna: se registra en el historial del ticket y <strong>no se envía al cliente</strong>.</span>
                  </div>
                )}
                <div className="tk-editor-wrap">
                  <ReactQuill
                    value={notaBody}
                    onChange={setNotaBody}
                    readOnly={esTerminado}
                    modules={{ toolbar: [[{ header: [false, 2, 3] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                    placeholder={esTerminado ? 'El ticket está terminado. Reábralo para registrar notas…' : 'Escriba una nota interna para el equipo…'}
                  />
                </div>
                {notaAdjuntos.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {notaAdjuntos.map((a) => (
                      <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 8px', background: 'var(--tk-slate-100)', borderRadius: 4, fontSize: 11 }}>
                        <FileText style={{ width: 11, height: 11 }} />
                        {a.nombre}
                        <button onClick={() => setNotaAdjuntos((p) => p.filter((x) => x.id !== a.id))} style={{ background: 'transparent', border: 0, cursor: 'pointer' }}>
                          <X style={{ width: 11, height: 11, color: 'var(--tk-rose)' }} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="tk-composer-row">
                  <div className="tk-ed-actions">
                    <input ref={notaFileInputRef} type="file" multiple onChange={subirAdjuntoNota} style={{ display: 'none' }} data-testid="input-adjunto-nota" />
                    <button onClick={() => notaFileInputRef.current?.click()} disabled={notaUploading || esTerminado} data-testid="btn-adjuntar-nota">
                      {notaUploading ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Paperclip style={{ width: 12, height: 12 }} />}
                      Adjuntar
                    </button>
                  </div>
                  <button className="tk-btn-primary-lg" onClick={guardarNota} disabled={notaBusy || esTerminado} title={esTerminado ? 'El ticket está terminado. Reábralo para registrar notas.' : undefined} data-testid="btn-guardar-nota">
                    {notaBusy ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : <StickyNote style={{ width: 13, height: 13 }} />}
                    Guardar nota
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'auditoria' && (
              <div style={{ maxHeight: 280, overflow: 'auto' }}>
                <AuditTable items={auditItems || []} loading={auditLoading} hideTicketColumn />
              </div>
            )}
              </>
            )}
          </div>

          {giOpen && (
            <div className="tk-gi-overlay" data-testid="gestion-interna-overlay">
              <div className="tk-gi-overlay-head">
                <span className="tk-gi-overlay-title">
                  <Share2 style={{ width: 15, height: 15 }} /> Gestión Interna
                </span>
                <button
                  type="button"
                  className="tk-gi-overlay-close"
                  onClick={() => setGiOpen(false)}
                  data-testid="btn-cerrar-gestion-interna"
                  title="Cerrar Gestión Interna"
                >
                  <X style={{ width: 16, height: 16 }} />
                </button>
              </div>
              <div className="tk-gi-overlay-body">
                <GestionInternaPanel
                  gestiones={gestiones}
                  puedeDerivar={puedeDerivar}
                  esTerminado={esTerminado}
                  sinResponsable={sinResponsable}
                  responsablesInternos={responsablesInternos}
                  prioridadesInternas={prioridadesInternas}
                  derivOpen={derivOpen} setDerivOpen={setDerivOpen}
                  derivResp={derivResp} setDerivResp={setDerivResp}
                  derivPrioridad={derivPrioridad} setDerivPrioridad={setDerivPrioridad}
                  derivNota={derivNota} setDerivNota={setDerivNota}
                  derivando={derivando} onDerivar={derivarGestion}
                  gestionMsg={gestionMsg} setGestionMsg={setGestionMsg}
                  gestionBusy={gestionBusy}
                  gestionAdjuntos={gestionAdjuntos} gestionUploading={gestionUploading}
                  onSubirAdjunto={subirAdjuntoGestion} onQuitarAdjunto={quitarAdjuntoGestion}
                  onResponder={responderGestion} onCerrar={cerrarGestion}
                  onResponderYCerrar={responderYCerrarGestion}
                  onVerAdjunto={verAdjunto}
                  user={user}
                />
              </div>
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        {sidebarOpen && (
          <>
            <div className="tk-side-scrim" data-testid="sidebar-scrim" onClick={() => setSidebarOpen(false)} />
            <aside className="tk-side-col" data-testid="sidebar-drawer">
              <button className="tk-side-close" onClick={() => setSidebarOpen(false)} title="Cerrar panel" data-testid="btn-close-sidebar-mobile">
                <X style={{ width: 16, height: 16 }} />
              </button>
            {sidebarReadOnly && (
              <div className="tk-side-readonly-note" data-testid="sidebar-readonly-note" title={configLockTooltip}>
                <Lock style={{ width: 12, height: 12 }} />
                {hayGestionAbierta
                  ? 'Para editar el ticket debe cerrar las gestiones internas abiertas.'
                  : 'Edición del ticket pausada mientras gestionas internamente.'}
              </div>
            )}
            {!gestionTabActive && (
              <StateControl ticket={t} onChange={cambiarEstado} onIniciarCierre={iniciarCierre} bloqueoGestion={hayGestionAbierta} />
            )}

            {hayGestionAbierta && (
              <Accordion title="SLA Interno" icon={Clock} open={openAcc.slaInterno !== false} onToggle={() => setOpenAcc((p) => ({ ...p, slaInterno: p.slaInterno === false }))} testid="acc-sla-interno">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="sidebar-sla-interno">
                  {gestionesAbiertas.map((g) => (
                    <div key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tk-text)' }}>
                        {g.area_funcional_nombre || g.responsable_interno_nombre || 'Gestión'}
                      </div>
                      <GestionSlaBar gestion={g} />
                    </div>
                  ))}
                </div>
              </Accordion>
            )}

            <Accordion title="Detalles" icon={UserIcon} open={openAcc.detalles} onToggle={() => setOpenAcc((p) => ({ ...p, detalles: !p.detalles }))} testid="acc-detalles">
              <KV label="Cliente" value={t.cliente_email} />
              {Array.isArray(t.cliente_unidades) && t.cliente_unidades.length > 0 && (
                <div className="tk-kv" data-testid="kv-unidad">
                  <span className="tk-k">Unidad</span>
                  <span className="tk-v" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {t.cliente_unidades.map((u, i) => (
                      <span key={i} title={`${u.nombre_proyecto} · ${u.codigo}`}>
                        {`{${u.nombre_proyecto}, ${u.codigo}}`}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              <KV label="Canal" value={canal?.nombre || 'Correo'} />
              <KV label="Apertura" value={fmtFecha(t.fecha_apertura)} />
              {t.fecha_asignacion && <KV label="Asignado" value={fmtFecha(t.fecha_asignacion)} />}
              {t.fecha_primera_resp && <KV label="1ra resp." value={fmtFecha(t.fecha_primera_resp)} />}
              {t.fecha_cierre && <KV label="Cierre" value={fmtFecha(t.fecha_cierre)} />}
            </Accordion>

            <Accordion title="SLA" icon={Shield} open={openAcc.sla} onToggle={() => setOpenAcc((p) => ({ ...p, sla: !p.sla }))} testid="acc-sla">
              <SlaBlock ticket={t} prioridad={pri} />
            </Accordion>

            {t.canal_interaccion && (
              <Accordion
                title={(t.canal_interaccion.codigo_canal || '').toUpperCase() === 'WHATSAPP' ? 'Interacción WhatsApp' : 'Interacción telefónica'}
                icon={Phone}
                open={openAcc.canalInteraccion !== false}
                onToggle={() => setOpenAcc((p) => ({ ...p, canalInteraccion: p.canalInteraccion === false }))}
                testid="acc-canal-interaccion"
              >
                <CanalInteraccionPanel canal={t.canal_interaccion} />
              </Accordion>
            )}
            {t.canal_interaccion && (
              <div style={{ borderTop: '1px solid var(--tk-border, #e2e8f0)', margin: '2px 0' }} data-testid="divider-canal-clasificacion" />
            )}

            <Accordion title="Clasificación" icon={Folder} open={openAcc.clasificacion} onToggle={() => setOpenAcc((p) => ({ ...p, clasificacion: !p.clasificacion }))} testid="acc-clasificacion">
              <ClasificacionPanel
                ticket={t} tree={tree}
                editando={editandoClasificacion}
                readOnly={sidebarReadOnly}
                readOnlyTooltip={configLockTooltip}
                onIniciarEdicion={() => setEditandoClasificacion(true)}
                onCancelar={() => setEditandoClasificacion(false)}
                onConfirmar={guardarClasificacion}
              />
            </Accordion>

            <Accordion title="Asignación" icon={UserCheck} open={openAcc.asignacion} onToggle={() => setOpenAcc((p) => ({ ...p, asignacion: !p.asignacion }))} testid="acc-asignacion">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div className="tk-label">Responsable</div>
                  <Select value={t.responsable_id || 'unassigned'} onValueChange={(v) => patchTicket({ responsable_id: v === 'unassigned' ? null : v })} disabled={sidebarReadOnly}>
                    <SelectTrigger className="h-8 text-xs" data-testid="select-responsable" title={configLockTooltip}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">— Sin asignar —</SelectItem>
                      {responsablesConActual.map((r) => <SelectItem key={r.id} value={r.id}>{r.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {respNombre && (
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--tk-text-muted)' }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', background: colorForString(respNombre), color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {initialsOf(respNombre)}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resp?.correo || respNombre}</span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="tk-label">Prioridad</div>
                  <Select value={t.prioridad_id || 'none'} onValueChange={(v) => patchTicket({ prioridad_id: v === 'none' ? null : v })} disabled={sidebarReadOnly}>
                    <SelectTrigger className="h-8 text-xs" data-testid="select-prioridad" title={configLockTooltip}>
                      {pri ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: pri.color }} />{pri.nombre}</span> : <SelectValue placeholder="Sin prioridad" />}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sin prioridad —</SelectItem>
                      {prioridades.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color }} />
                            {p.nombre} · {p.horas_sla}h
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="tk-label">Canal</div>
                  <Select value={t.canal_id} onValueChange={(v) => patchTicket({ canal_id: v })} disabled={sidebarReadOnly}>
                    <SelectTrigger className="h-8 text-xs" data-testid="select-canal-detalle" title={configLockTooltip}><SelectValue /></SelectTrigger>
                    <SelectContent>{canales.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </Accordion>

            <Accordion title={`Adjuntos · ${adjuntosTicket.length}`} icon={Paperclip} open={openAcc.adjuntos} onToggle={() => setOpenAcc((p) => ({ ...p, adjuntos: !p.adjuntos }))} testid="acc-adjuntos">
              {adjuntosTicket.length === 0 ? (
                <p style={{ fontSize: 11, color: 'var(--tk-text-soft)', fontStyle: 'italic' }}>Sin adjuntos en este ticket.</p>
              ) : adjuntosTicket.map((a) => (
                <button key={a.id} className="tk-file-row" onClick={() => verAdjunto(a)} data-testid={`adj-side-${a.id}`}>
                  <span className="tk-file-icon"><FileText style={{ width: 14, height: 14 }} /></span>
                  <div className="tk-file-meta">
                    <div className="tk-file-name">{a.nombre_original}</div>
                    {a.size_bytes && <div className="tk-file-size">{(a.size_bytes / 1024).toFixed(1)} KB</div>}
                  </div>
                  <Download style={{ width: 13, height: 13, color: 'var(--tk-text-muted)' }} />
                </button>
              ))}
            </Accordion>

            <Accordion
              title="Auditoría"
              icon={ClipboardList}
              open={openAcc.auditoria}
              onToggle={() => {
                setOpenAcc((p) => {
                  if (!p.auditoria) cargarAuditoria();
                  return { ...p, auditoria: !p.auditoria };
                });
              }}
              testid="acc-auditoria"
            >
              <div style={{ fontSize: 11 }}>
                <AuditTable items={auditItems || []} loading={auditLoading} hideTicketColumn scrollable />
              </div>
            </Accordion>
          </aside>
          </>
        )}
      </div>

      <AlertDialog open={respFinalOpen} onOpenChange={setRespFinalOpen}>
        <AlertDialogContent data-testid="respuesta-final-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Enviar respuesta final y cerrar</AlertDialogTitle>
            <AlertDialogDescription>
              Se enviará tu respuesta al cliente como <strong>correo de cierre</strong> (incluyendo tu comentario y los archivos adjuntos) y el ticket pasará a estado <strong>Terminado</strong>. Esta acción no se puede deshacer desde aquí.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={respFinalBusy} data-testid="respuesta-final-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); enviarRespuestaFinal(); }}
              disabled={respFinalBusy}
              data-testid="respuesta-final-confirm"
            >
              {respFinalBusy ? 'Enviando…' : 'Responder y Terminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={reabrirOpen} onOpenChange={(o) => { if (!reabriendo) setReabrirOpen(o); }}>
        <AlertDialogContent data-testid="reabrir-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reabrir ticket</AlertDialogTitle>
            <AlertDialogDescription>
              El ticket pasará de <strong>Terminado</strong> a <strong>En proceso</strong>. Indica el <strong>motivo de la reapertura</strong>: se registrará como nota en el historial del ticket.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={motivoReapertura}
            onChange={(e) => setMotivoReapertura(e.target.value)}
            rows={3}
            placeholder="Ej.: El cliente respondió con información adicional / se requiere revisar la solución brindada…"
            className="text-sm"
            data-testid="motivo-reapertura-input"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reabriendo} data-testid="reabrir-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmarReapertura(); }}
              disabled={reabriendo || !motivoReapertura.trim()}
              data-testid="reabrir-confirm"
            >
              {reabriendo ? 'Reabriendo…' : 'Reabrir ticket'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =====================================================
// SUB-COMPONENTES
// =====================================================

/** Detecta si un HTML viene como documento de email completo (Outlook/Gmail/auto-reply).
 *  Heurística robusta: doctype / <html / <body / ó 2+ <table>. */
function _esEmailCompleto(html) {
  if (!html) return false;
  const s = html.toLowerCase();
  if (s.includes('<!doctype') || s.includes('<html') || s.includes('<body')) return true;
  // Dos o más tablas anidadas → muy probablemente layout email (Outlook style)
  const tableMatches = s.match(/<table\b/g);
  return !!tableMatches && tableMatches.length >= 2;
}

/** Renderiza HTML de email en iframe sandbox aislado, con altura auto-ajustada.
 *  Espejo del comportamiento de Gmail/Outlook web: el email conserva su tipografía,
 *  paddings y tablas sin ser contaminado por el CSS de la app (y viceversa).
 */
function EmailHtmlPreview({ html }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(140);
  const [showRemote, setShowRemote] = useState(false);

  // Origen de nuestro backend (donde viven las imágenes inline a través del proxy firmado).
  const backendOrigin = useMemo(() => {
    try { return new URL(process.env.REACT_APP_BACKEND_URL).origin; } catch { return ''; }
  }, []);

  // ¿El correo trae imágenes remotas de terceros (posibles tracking-pixels)?
  const tieneImagenesRemotas = useMemo(() => {
    const re = /<img\b[^>]*\bsrc\s*=\s*["']https?:\/\/([^"']+)/gi;
    let match;
    while ((match = re.exec(html || '')) !== null) {
      const url = match[1];
      // Las que apuntan a nuestro backend (inline) NO cuentan como remotas.
      if (!backendOrigin || !`https://${url}`.startsWith(backendOrigin)) return true;
    }
    return false;
  }, [html, backendOrigin]);

  // Sanitizar con WHOLE_DOCUMENT para conservar <html>/<head>/<body> intactos.
  // Defensa en profundidad: aunque el sandbox bloquea scripts, prohibimos
  // explícitamente tags/atributos peligrosos.
  const safeHtml = useMemo(() => DOMPurify.sanitize(html || '', {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ['style'],
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus'],
  }), [html]);

  // CSP dentro del documento: por defecto solo permite imágenes de nuestro backend
  // + data: (bloquea tracking-pixels remotos). El toggle habilita https: para todas.
  const csp = useMemo(() => {
    const imgSrc = showRemote
      ? `img-src ${backendOrigin} https: data:`
      : `img-src ${backendOrigin} data:`;
    return `default-src 'none'; ${imgSrc}; style-src 'unsafe-inline'; font-src data:`;
  }, [showRemote, backendOrigin]);

  // Inyectar CSP + <base target="_blank"> en el <head>.
  const srcDoc = useMemo(() => {
    const headInject = `<meta http-equiv="Content-Security-Policy" content="${csp}"><base target="_blank">`;
    if (/<head[^>]*>/i.test(safeHtml)) {
      return safeHtml.replace(/<head([^>]*)>/i, `<head$1>${headInject}`);
    }
    return `<head>${headInject}</head>${safeHtml}`;
  }, [safeHtml, csp]);

  // Auto-resize: medir contentDocument tras load. El email es contenido
  // estático, basta con medir en load + reintentos diferidos para fonts/imágenes.
  // (Evitamos ResizeObserver para no caer en "ResizeObserver loop completed".)
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let timers = [];
    const measure = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const h = Math.max(
          doc.documentElement.scrollHeight,
          doc.body ? doc.body.scrollHeight : 0
        );
        if (h) setHeight((prev) => (Math.abs(h - prev) > 1 ? h : prev));
      } catch { /* noop */ }
    };

    const onLoad = () => {
      measure();
      // Re-medir tras 100ms y 600ms (cubre fonts custom + imágenes tardías)
      timers.push(setTimeout(measure, 100));
      timers.push(setTimeout(measure, 600));
    };

    iframe.addEventListener('load', onLoad);
    if (iframe.contentDocument?.readyState === 'complete') onLoad();

    return () => {
      iframe.removeEventListener('load', onLoad);
      timers.forEach((t) => clearTimeout(t));
      timers = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcDoc]);

  return (
    <div className="tk-tl-email-frame" data-testid="tk-tl-email-frame">
      {tieneImagenesRemotas && !showRemote && (
        <div
          data-testid="tk-remote-images-banner"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            padding: '7px 11px', marginBottom: 8, borderRadius: 8,
            background: 'var(--tk-amber-soft, #fef3c7)', border: '1px solid var(--tk-amber, #f59e0b)',
            fontSize: 12, color: 'var(--tk-text-muted, #475569)',
          }}
        >
          <span>Se bloquearon imágenes remotas de este correo para proteger tu privacidad.</span>
          <button
            type="button"
            onClick={() => setShowRemote(true)}
            data-testid="tk-mostrar-remotas-btn"
            style={{
              flexShrink: 0, fontWeight: 600, fontSize: 12, padding: '3px 10px', borderRadius: 6,
              border: '1px solid var(--tk-amber, #f59e0b)', background: '#fff',
              color: 'var(--tk-amber-strong, #b45309)', cursor: 'pointer',
            }}
          >
            Mostrar imágenes
          </button>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="Vista del correo"
        srcDoc={srcDoc}
        sandbox="allow-same-origin allow-popups"
        className="ph-no-capture"
        data-ph-capture="false"
        style={{ width: '100%', height: `${height}px`, border: 0, display: 'block' }}
      />
    </div>
  );
}

function TimelineMessage({ mensaje: m, verAdjunto }) {
  const esCliente = m.autor_tipo === 'CLIENTE' || m.direccion === 'ENTRANTE';
  const esSistema = m.direccion === 'SISTEMA';
  const html = m.cuerpo_html || `<p>${(m.cuerpo_texto || '').replace(/\n/g, '<br/>')}</p>`;
  const cleanHtmlInline = DOMPurify.sanitize(html, { ADD_TAGS: ['style'] });
  const tieneInline = (m.adjuntos || []).some((a) => a.es_inline);
  const isEmail = _esEmailCompleto(m.cuerpo_html) || tieneInline;
  const adj = (m.adjuntos || []).filter((a) => !a.es_inline);

  if (esSistema) {
    return (
      <div className="tk-tl-msg sistema">
        <div className="tk-tl-avatar" style={{ background: 'var(--tk-amber)' }}>
          <StickyNote style={{ width: 18, height: 18 }} />
        </div>
        <div className="tk-tl-head">
          <span className="tk-tl-name">Nota interna <span className="tk-tl-mail">· {m.autor_email}</span></span>
          <span className="tk-tl-time">{fmtFecha(m.created_at)}</span>
        </div>
        <div className="tk-tl-body">
          <div dangerouslySetInnerHTML={{ __html: cleanHtmlInline }} />
        </div>
      </div>
    );
  }

  const author = m.autor_email || (esCliente ? 'Cliente' : 'Agente');
  const initials = initialsOf(author);
  const bgColor = esCliente ? '#64748b' : 'var(--tk-primary)';

  return (
    <div className={`tk-tl-msg ${esCliente ? 'cliente' : 'agente'}`}>
      <div className="tk-tl-avatar" style={{ background: bgColor }}>{initials}</div>
      <div className="tk-tl-head">
        <span className="tk-tl-name">
          {author}
          <span style={{ marginLeft: 8 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10, letterSpacing: 0.4,
              background: esCliente ? 'var(--tk-slate-100)' : 'var(--tk-primary-soft)',
              color: esCliente ? 'var(--tk-text-muted)' : 'var(--tk-primary)',
            }}>
              {esCliente ? 'CLIENTE' : 'AGENTE'}
            </span>
          </span>
          {m.es_tercero && (
            <span style={{ marginLeft: 6 }} data-testid={`badge-tercero-${m.id}`}>
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10, letterSpacing: 0.4,
                background: 'var(--tk-amber-soft, #fef3c7)', color: 'var(--tk-amber-strong, #b45309)',
              }}>
                COPIADO/TERCERO
              </span>
            </span>
          )}
        </span>
        <span className="tk-tl-time">{fmtFecha(m.created_at)}</span>
      </div>
      {esCliente && ((m.cc && m.cc.length > 0) || (m.to && m.to.length > 0)) && (
        <div
          data-testid={`msg-destinatarios-${m.id}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '2px 0 6px', fontSize: 11, color: 'var(--tk-text-soft)' }}
        >
          {m.to && m.to.length > 0 && (
            <div data-testid={`msg-to-${m.id}`}>
              <span style={{ fontWeight: 600 }}>Para: </span>
              {m.to.map((d) => d.email).join(', ')}
            </div>
          )}
          {m.cc && m.cc.length > 0 && (
            <div data-testid={`msg-cc-${m.id}`}>
              <span style={{ fontWeight: 600 }}>CC: </span>
              {m.cc.map((d) => d.email).join(', ')}
            </div>
          )}
        </div>
      )}
      <div className="tk-tl-body">
        {isEmail ? (
          <EmailHtmlPreview html={m.cuerpo_html} />
        ) : (
          <div className="ql-snow"><div className="ql-editor" style={{ padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: cleanHtmlInline }} /></div>
        )}
        {adj.length > 0 && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--tk-border-soft)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {adj.map((a) => (
              <button key={a.id} className="tk-tl-attach" onClick={() => verAdjunto(a)} data-testid={`adj-link-${a.id}`}>
                <FileText style={{ width: 12, height: 12 }} />
                <span>{a.nombre_original}</span>
                <Download style={{ width: 11, height: 11, marginLeft: 4 }} />
                {a.size_bytes && <span style={{ color: 'var(--tk-text-soft)' }}>({(a.size_bytes / 1024).toFixed(1)} KB)</span>}
              </button>
            ))}
          </div>
        )}
        {m.resend_id && m.direccion === 'SALIENTE' && (
          <ResendIdFootnote value={m.resend_id} />
        )}
      </div>
    </div>
  );
}

/** Pie discreto en mensajes SALIENTES con el resend_id (copy + link al panel). */
function ResendIdFootnote({ value }) {
  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { navigator.clipboard?.writeText(value); } catch { /* noop */ }
  };
  return (
    <div
      className="tk-tl-resend-footnote"
      data-testid="tk-resend-id"
      title="Identificador único de envío en Resend"
    >
      <span>Resend ID:</span>
      <code onClick={handleCopy} className="tk-tl-resend-code" title="Click para copiar">{value}</code>
      <a
        href={`https://resend.com/emails/${value}`}
        target="_blank"
        rel="noopener noreferrer"
        className="tk-tl-resend-link"
        title="Abrir en panel Resend"
      >↗</a>
    </div>
  );
}

function StateControl({ ticket, onChange, onIniciarCierre, bloqueoGestion = false }) {
  const info = ESTADOS_INFO[ticket.estado] || ESTADOS_INFO.NUEVO;

  // Requisitos para poder TERMINAR un ticket (espejo del backend):
  // responsable + prioridad + clasificación de nivel mínimo N2.
  const faltantesTerminar = [];
  if (!ticket.responsable_id) faltantesTerminar.push('responsable');
  if (!ticket.prioridad_id) faltantesTerminar.push('prioridad');
  if ((ticket.clasificacion_path_ids || []).length < 2) faltantesTerminar.push('clasificación (mínimo nivel 2)');

  return (
    <div className="tk-state-control" data-testid="state-control-panel">
      <div className="tk-state-control-label">Estado actual</div>
      <div className="tk-state-control-current">
        <span className="tk-state-dot-lg" />
        <span className="tk-state-control-name">{info.label.toUpperCase()}</span>
      </div>
      <div className="tk-state-control-actions">
        {['ASIGNADO', 'EN_PROCESO', 'TERMINADO'].map((k) => {
          const inf = ESTADOS_INFO[k];
          const Icon = inf.icon;
          const isCurrent = ticket.estado === k;
          const isSuccess = k === 'TERMINADO';
          const bloqueaTerminar = k === 'TERMINADO' && !isCurrent && faltantesTerminar.length > 0;
          const bloqueaPorGestion = bloqueoGestion && !isCurrent && k !== 'EN_PROCESO';
          const cls = [
            'tk-state-action-btn',
            isCurrent ? 'is-current' : '',
            isSuccess && !isCurrent ? 'is-success' : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={k}
              className={cls}
              disabled={isCurrent || bloqueaTerminar || bloqueaPorGestion}
              title={
                bloqueaPorGestion ? 'Para cambiar de estado debe cerrar las gestiones internas abiertas'
                : bloqueaTerminar ? `Para terminar falta: ${faltantesTerminar.join(', ')}` : undefined
              }
              onClick={() => (k === 'TERMINADO' ? onIniciarCierre?.() : onChange(k))}
              data-testid={`btn-state-${k}`}
              type="button"
            >
              <Icon />
              {inf.label}
            </button>
          );
        })}
      </div>
      {bloqueoGestion && (
        <div className="tk-state-control-hint" data-testid="bloqueo-gestion-hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Lock style={{ width: 11, height: 11 }} /> Para cambiar de estado debe cerrar las gestiones internas abiertas.
        </div>
      )}
      {!bloqueoGestion && faltantesTerminar.length > 0 && ticket.estado !== 'TERMINADO' && (
        <div className="tk-state-control-hint" data-testid="terminar-requisitos-hint">
          Para terminar el ticket falta: {faltantesTerminar.join(', ')}.
        </div>
      )}
    </div>
  );
}

function Accordion({ title, icon: Icon, open, onToggle, children, testid }) {
  return (
    <div className="tk-accordion">
      <button className={`tk-acc-header ${open ? 'open' : ''}`} onClick={onToggle} data-testid={testid}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          {Icon && <Icon style={{ width: 13, height: 13 }} />}
          {title}
        </span>
        <span className="tk-acc-arrow"><ChevronRight style={{ width: 13, height: 13 }} /></span>
      </button>
      <div className={`tk-acc-body ${open ? '' : 'closed'}`}>{children}</div>
    </div>
  );
}

function _fmtDuracion(segs) {
  if (segs == null) return '—';
  const m = Math.floor(segs / 60), s = segs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function CanalInteraccionPanel({ canal }) {
  const [verCompleta, setVerCompleta] = useState(false);
  if (!canal) return null;
  const esTelefono = (canal.codigo_canal || '').toUpperCase() === 'TELEFONO';
  const resuelto = canal.resuelto_en_interaccion;
  const badge = resuelto === true
    ? { txt: '● Resuelto en la interacción', color: '#166534', bg: 'var(--tk-green-soft, #dcfce7)' }
    : resuelto === false
      ? { txt: '▸ Requiere gestión posterior', color: '#78350f', bg: 'var(--tk-amber-soft, #fef3c7)' }
      : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="canal-interaccion-panel">
      {esTelefono && canal.num_telefono && (
        <div className="tk-kv">
          <span className="tk-k">Número de teléfono</span>
          <span className="tk-v" data-testid="canal-num-telefono" style={{ fontFamily: 'monospace' }}>{canal.num_telefono}</span>
        </div>
      )}
      <div className="tk-kv">
        <span className="tk-k">Duración</span>
        <span className="tk-v" data-testid="canal-duracion">{_fmtDuracion(canal.duracion_seg)}</span>
      </div>
      {badge && (
        <div className="tk-kv">
          <span className="tk-k">Resultado</span>
          <span data-testid="canal-resuelto-badge" style={{ fontSize: 11, fontWeight: 600, color: badge.color, background: badge.bg, padding: '2px 8px', borderRadius: 6 }}>
            {badge.txt}
          </span>
        </div>
      )}
      {esTelefono && canal.enlace_grabacion && (
        <div>
          <div className="tk-k" style={{ marginBottom: 4 }}>Grabación</div>
          <audio controls preload="none" src={canal.enlace_grabacion} data-testid="canal-grabacion-audio" style={{ width: '100%', height: 34 }} />
          <a href={canal.enlace_grabacion} target="_blank" rel="noreferrer" data-testid="canal-grabacion-link"
             style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--tk-text-muted)', marginTop: 4 }}>
            <Download style={{ width: 11, height: 11 }} /> Abrir / descargar
          </a>
        </div>
      )}
      {esTelefono && canal.transcripcion && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span className="tk-k">Transcripción</span>
            <button className="tk-btn-toolbar" data-testid="canal-transcripcion-toggle" onClick={() => setVerCompleta((v) => !v)} style={{ fontSize: 11 }}>
              {verCompleta ? 'Ocultar' : 'Ver completa'}
            </button>
          </div>
          <div data-testid="canal-transcripcion"
               style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--tk-text)', background: 'var(--tk-bg-soft, #f8fafc)', border: '1px solid var(--tk-border, #e2e8f0)', borderRadius: 6, padding: 8, maxHeight: verCompleta ? 220 : 60, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
            {canal.transcripcion}
          </div>
        </div>
      )}
      {!esTelefono && canal.primera_respuesta_seg != null && (
        <div className="tk-kv">
          <span className="tk-k">1ª respuesta</span>
          <span className="tk-v" data-testid="canal-primera-respuesta">{_fmtDuracion(canal.primera_respuesta_seg)}</span>
        </div>
      )}
      {canal.id_interaccion && (
        <div className="tk-kv">
          <span className="tk-k">ID interacción</span>
          <span className="tk-v" data-testid="canal-id-interaccion" title={canal.id_interaccion} style={{ fontFamily: 'monospace', fontSize: 11 }}>{canal.id_interaccion}</span>
        </div>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="tk-kv">
      <span className="tk-k">{label}</span>
      <span className="tk-v" title={value}>{value || '—'}</span>
    </div>
  );
}

function SlaBlock({ ticket: t, prioridad: pri }) {
  const sla = getSlaState(t);
  if (!sla && !t.fecha_vencimiento_sla) {
    return <p style={{ fontSize: 11, color: 'var(--tk-text-soft)', fontStyle: 'italic' }}>Sin SLA configurado (prioridad sin horas).</p>;
  }
  const tone =
    sla?.key === 'VENCIDO' ? 'late' :
    sla?.key === 'WARN' ? 'warn' :
    sla?.key === 'OK' ? 'ok' :
    sla?.key === 'CUMPLIDO' ? 'cumplido' :
    sla?.key === 'INCUMPLIDO' ? 'incumplido' : '';

  // Progreso visual
  // Reglas: % consumido = (total - restante) / total * 100
  //   > 75% queda → Verde   (≤ 25% consumido)
  //   25-75% queda → Verde  (25-75% consumido)
  //   < 25% queda → Amarillo  (>75% consumido)
  //   vencido     → Rojo    (100% + texto "Plazo vencido")
  // En TERMINADO: barra estática 100% verde (cumplido) o roja (incumplido)
  const totales = typeof t.sla_segs_totales === 'number' ? t.sla_segs_totales : null;
  const restantes = typeof t.sla_segs_restantes === 'number' ? t.sla_segs_restantes : null;

  let progressPct = null;
  let progressTone = tone;
  let progressLabel = '';

  if (t.estado === 'TERMINADO') {
    if (t.sla_cumplido === true) {
      progressPct = 100;
      progressTone = 'cumplido';
      progressLabel = '100% · SLA cumplido';
    } else if (t.sla_cumplido === false) {
      progressPct = 100;
      progressTone = 'incumplido';
      progressLabel = '100% · SLA incumplido';
    }
  } else if (totales && totales > 0 && restantes !== null) {
    if (restantes <= 0) {
      progressPct = 100;
      progressTone = 'late';
      progressLabel = 'Plazo vencido';
    } else {
      const consumido = Math.max(0, Math.min(100, ((totales - restantes) / totales) * 100));
      progressPct = consumido;
      // Threshold por porcentaje restante
      const restPct = (restantes / totales) * 100;
      if (restPct < 25) progressTone = 'warn';
      else progressTone = 'ok';
      progressLabel = `${Math.round(consumido)}% del plazo consumido`;
    }
  }

  return (
    <div className={`tk-sla-block ${tone}`}>
      <div className="tk-sla-block-num">{sla?.detalle?.split(' ').slice(0, 2).join(' ') || sla?.label}</div>
      <div className="tk-sla-block-text">
        {t.fecha_vencimiento_sla ? `Vence: ${formatVencimiento(t.fecha_vencimiento_sla)}` : '—'}
        {pri && ` · ${pri.nombre} (${pri.horas_sla}h)`}
      </div>
      {progressPct !== null && (
        <div className="tk-sla-progress-wrap" data-testid="sla-progress-bar">
          <div className={`tk-sla-progress-track tk-sla-tone-${progressTone}`}>
            <div
              className="tk-sla-progress-fill"
              style={{ width: `${progressPct}%` }}
              data-testid="sla-progress-fill"
              data-pct={Math.round(progressPct)}
            />
          </div>
          <div className="tk-sla-progress-label" data-testid="sla-progress-label">
            {progressLabel}
          </div>
        </div>
      )}
    </div>
  );
}

function ClasificacionPanel({ ticket: t, tree, editando, readOnly = false, readOnlyTooltip, onIniciarEdicion, onCancelar, onConfirmar }) {
  const terminado = t.estado === 'TERMINADO';
  const ruta = t.clasificacion_ruta;
  const tieneNodoActivo = !!t.clasificacion_id;
  const huerfana = !!ruta && !tieneNodoActivo;
  const puedeEditar = !terminado && !readOnly;

  if (editando && !readOnly) return <ClasificacionCascadeSelect tree={tree} value={t.clasificacion_id} onChange={onConfirmar} onCancel={onCancelar} />;
  if (!ruta && !tieneNodoActivo) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--tk-text-soft)' }}>Sin clasificar</span>
        {puedeEditar && (
          <button className="tk-btn-toolbar" onClick={onIniciarEdicion} data-testid="btn-clasificar" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Edit2 style={{ width: 11, height: 11 }} /> Clasificar
          </button>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} title={readOnly ? readOnlyTooltip : undefined}>
      <div style={{ fontSize: 12, lineHeight: 1.4, color: huerfana ? 'var(--tk-text-muted)' : 'var(--tk-text)', fontStyle: huerfana ? 'italic' : 'normal' }} data-testid="clasificacion-snapshot">
        {ruta}
      </div>
      {huerfana && (
        <span style={{ display: 'inline-block', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, background: 'var(--tk-amber-soft)', color: '#78350f', padding: '2px 7px', borderRadius: 4, alignSelf: 'flex-start' }}>
          Original eliminado
        </span>
      )}
      {puedeEditar && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="tk-btn-toolbar" onClick={onIniciarEdicion} data-testid="btn-reclasificar" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Edit2 style={{ width: 11, height: 11 }} /> {huerfana ? 'Reclasificar' : 'Cambiar'}
          </button>
        </div>
      )}
    </div>
  );
}


// =====================================================
// GESTIÓN INTERNA (Bloque B)
// =====================================================
function _fmtRestante(secs) {
  if (secs <= 0) {
    const v = Math.abs(secs);
    const d = Math.floor(v / 86400), h = Math.floor((v % 86400) / 3600);
    return d > 0 ? `${d}d ${h}h vencido` : `${h}h vencido`;
  }
  const d = Math.floor(secs / 86400), h = Math.floor((secs % 86400) / 3600), m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h restantes`;
  if (h > 0) return `${h}h ${m}m restantes`;
  return `${m}m restantes`;
}

function _iniciales(nombre) {
  if (!nombre) return '?';
  const p = String(nombre).trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
}

function GestionSlaBar({ gestion: g }) {
  if (g.estado_interno === 'CERRADO') {
    return (
      <div className="tk-sla-progress-wrap" data-testid={`gi-sla-${g.id}`}>
        <div className="tk-sla-progress-track tk-sla-tone-cumplido">
          <div className="tk-sla-progress-fill" style={{ width: '100%' }} data-pct={100} />
        </div>
        <div className="tk-sla-progress-label">Gestión cerrada</div>
      </div>
    );
  }
  if (!g.fecha_vencimiento_sla || !g.fecha_derivacion) {
    return <p style={{ fontSize: 11, color: 'var(--tk-text-soft)', fontStyle: 'italic', margin: 0 }}>Sin SLA interno configurado.</p>;
  }
  const ini = new Date(g.fecha_derivacion).getTime();
  const fin = new Date(g.fecha_vencimiento_sla).getTime();
  const now = Date.now();
  const total = Math.max(1, fin - ini);
  const restanteSecs = Math.round((fin - now) / 1000);
  let pct, tone, label;
  if (restanteSecs <= 0) {
    pct = 100; tone = 'late'; label = `Plazo vencido · ${_fmtRestante(restanteSecs)}`;
  } else {
    pct = Math.max(0, Math.min(100, ((now - ini) / total) * 100));
    tone = g.semaforo === 'ambar' ? 'warn' : 'ok';
    label = `${Math.round(pct)}% consumido · ${_fmtRestante(restanteSecs)}`;
  }
  return (
    <div className="tk-sla-progress-wrap" data-testid={`gi-sla-${g.id}`}>
      <div className={`tk-sla-progress-track tk-sla-tone-${tone}`}>
        <div className="tk-sla-progress-fill" style={{ width: `${pct}%` }} data-pct={Math.round(pct)} />
      </div>
      <div className="tk-sla-progress-label">{label}</div>
    </div>
  );
}

function GestionCard({ g, gestionMsg, setGestionMsg, gestionBusy, gestionAdjuntos, gestionUploading, onSubirAdjunto, onQuitarAdjunto, onResponder, onCerrar, onResponderYCerrar, onVerAdjunto, user }) {
  const abierto = g.estado_interno === 'ABIERTO';
  const puedeCerrar = user?.id === g.responsable_interno_id || ['ADMINISTRADOR', 'SUPERADMIN'].includes(user?.rol);
  const puedeResponder = puedeCerrar || user?.id === g.derivado_por_id;
  const busy = gestionBusy === g.id;
  const subiendo = gestionUploading === g.id;
  const adjDraft = (gestionAdjuntos && gestionAdjuntos[g.id]) || [];
  const msgs = g.mensajes || [];
  const fileRef = useRef(null);
  const puedeEnviar = !!(gestionMsg[g.id] || '').trim() || adjDraft.length > 0;
  const tieneTexto = !!(gestionMsg[g.id] || '').trim();
  return (
    <div className="tk-gi-card" data-testid={`gi-card-${g.id}`}>
      <div className="tk-gi-card-head">
        <div className="tk-gi-head-left">
          <span className="tk-gi-avatar">
            {_iniciales(g.responsable_interno_nombre)}
            <span className={`tk-gi-dot tk-gi-dot-${g.semaforo || 'sin_sla'}`} title="SLA interno" />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="tk-gi-resp">{g.responsable_interno_nombre || '—'}</div>
            <div className="tk-gi-area">{g.area_funcional_nombre || 'Sin área'}{g.prioridad_interna_nombre ? ` · ${g.prioridad_interna_nombre}` : ''}</div>
          </div>
        </div>
        <span className={`tk-gi-estado ${abierto ? 'is-open' : 'is-closed'}`} data-testid={`gi-estado-${g.id}`}>
          {abierto ? 'Abierto' : 'Cerrado'}
        </span>
      </div>

      <GestionSlaBar gestion={g} />

      <div className="tk-gi-thread">
        {msgs.length === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--tk-text-soft)', fontStyle: 'italic', margin: '4px 14px' }}>Sin mensajes en esta gestión.</p>
        ) : msgs.map((m) => {
          const esInterno = m.autor_usuario_id && m.autor_usuario_id === g.responsable_interno_id;
          const rolLabel = esInterno ? 'Responsable interno' : 'Responsable del ticket';
          const autorNombre = esInterno
            ? (g.responsable_interno_nombre || m.autor_email)
            : (m.autor_usuario_id === g.derivado_por_id ? (g.derivado_por_nombre || m.autor_email) : (m.autor_email || 'Sistema'));
          const hasBody = (m.cuerpo_texto && m.cuerpo_texto !== '(adjunto)') || (m.cuerpo_html || '').trim();
          const adjuntos = (m.adjuntos || []).filter((a) => !a.es_inline);
          return (
            <div key={m.id} className={`tk-gi-msg ${esInterno ? 'is-interno' : 'is-ticket'}`} data-testid={`gi-msg-${m.id}`}>
              <span className={`tk-gi-msg-avatar ${esInterno ? 'is-interno' : 'is-ticket'}`}>{_iniciales(autorNombre)}</span>
              <div className="tk-gi-msg-main">
                <div className="tk-gi-msg-top">
                  <span className="tk-gi-msg-author">{autorNombre}</span>
                  <span className={`tk-gi-rol-badge ${esInterno ? 'is-interno' : 'is-ticket'}`}>{rolLabel}</span>
                  <span className="tk-gi-msg-date">{fmtFecha(m.created_at)}</span>
                </div>
                {hasBody ? (
                  <div className="tk-gi-msg-body">{m.cuerpo_texto || (m.cuerpo_html || '').replace(/<[^>]+>/g, ' ').trim()}</div>
                ) : null}
                {adjuntos.length > 0 && (
                  <div className="tk-gi-adj-grid">
                    {adjuntos.map((a) => (
                      a.preview_url ? (
                        <button key={a.id} className="tk-gi-adj-thumb" onClick={() => onVerAdjunto(a)} data-testid={`gi-adj-${a.id}`} type="button" title={a.nombre_original}>
                          <img src={a.preview_url} alt={a.nombre_original} loading="lazy" />
                        </button>
                      ) : (
                        <button key={a.id} className="tk-gi-adj-row" onClick={() => onVerAdjunto(a)} data-testid={`gi-adj-${a.id}`} type="button">
                          <FileText style={{ width: 13, height: 13 }} />
                          <span className="tk-gi-adj-name">{a.nombre_original}</span>
                          {a.size_bytes ? <span className="tk-gi-adj-size">{(a.size_bytes / 1024).toFixed(0)} KB</span> : null}
                          <Download style={{ width: 12, height: 12 }} />
                        </button>
                      )
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {abierto ? (
        <div className="tk-gi-reply">
          {puedeResponder && (
            <Textarea
              value={gestionMsg[g.id] || ''}
              onChange={(e) => setGestionMsg((p) => ({ ...p, [g.id]: e.target.value }))}
              rows={2}
              placeholder="Escribe un mensaje en esta gestión interna…"
              className="text-sm"
              data-testid={`gi-reply-input-${g.id}`}
            />
          )}
          {puedeResponder && adjDraft.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {adjDraft.map((a) => (
                <span key={a.id} className="tk-gi-adj-chip" data-testid={`gi-adj-chip-${a.id}`}>
                  <FileText style={{ width: 11, height: 11 }} />
                  {a.nombre}
                  <button onClick={() => onQuitarAdjunto(g.id, a.id)} type="button" style={{ background: 'transparent', border: 0, cursor: 'pointer', lineHeight: 0 }}>
                    <X style={{ width: 11, height: 11, color: 'var(--tk-rose)' }} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 8 }}>
            {puedeResponder ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  ref={fileRef} type="file" multiple style={{ display: 'none' }}
                  onChange={(e) => onSubirAdjunto(g.id, e)}
                  data-testid={`gi-file-input-${g.id}`}
                />
                <button className="tk-gi-btn-adjuntar" onClick={() => fileRef.current?.click()} disabled={subiendo} data-testid={`gi-adjuntar-${g.id}`} type="button">
                  {subiendo ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Paperclip style={{ width: 12, height: 12 }} />}
                  Adjuntar
                </button>
              </div>
            ) : <span />}
            {puedeResponder && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  className="tk-btn-primary-lg"
                  onClick={() => onResponder(g.id)}
                  disabled={busy || subiendo || !puedeEnviar}
                  data-testid={`gi-enviar-${g.id}`}
                  type="button"
                >
                  {busy ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 13, height: 13 }} />}
                  Enviar
                </button>
                {puedeCerrar && (
                  <button
                    className="tk-gi-btn-cerrar"
                    onClick={() => onResponderYCerrar(g.id)}
                    disabled={busy || subiendo || !tieneTexto}
                    title={!tieneTexto ? 'Escribe una respuesta para poder cerrar la gestión.' : 'Enviar la respuesta y cerrar la gestión'}
                    data-testid={`gi-responder-cerrar-${g.id}`}
                    type="button"
                  >
                    <CheckCheck style={{ width: 13, height: 13 }} /> Responder y Cerrar
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="tk-gi-cerrada-info" data-testid={`gi-cerrada-info-${g.id}`}>
          <CheckCheck style={{ width: 12, height: 12 }} /> Cerrada por {g.cerrado_por_nombre || '—'} · {fmtFecha(g.fecha_cierre)}
        </div>
      )}
    </div>
  );
}

function GestionInternaPanel({
  gestiones, puedeDerivar, esTerminado, sinResponsable, responsablesInternos, prioridadesInternas,
  derivOpen, setDerivOpen, derivResp, setDerivResp, derivPrioridad, setDerivPrioridad,
  derivNota, setDerivNota, derivando, onDerivar,
  gestionMsg, setGestionMsg, gestionBusy, gestionAdjuntos, gestionUploading,
  onSubirAdjunto, onQuitarAdjunto, onResponder, onCerrar, onResponderYCerrar, onVerAdjunto, user,
}) {
  return (
    <div className="tk-gi-panel" data-testid="gestion-interna-panel">
      <div className="tk-gi-panel-head">
        <span className="tk-gi-panel-title">
          <Share2 style={{ width: 13, height: 13 }} /> Derivaciones internas
          <span className="tk-gi-panel-count">{gestiones.length}</span>
        </span>
        {puedeDerivar && !esTerminado && !sinResponsable && (
          <button className="tk-btn-ghost" onClick={() => setDerivOpen((v) => !v)} data-testid="btn-abrir-derivar" type="button">
            {derivOpen ? 'Cancelar' : 'Derivar a usuario'}
          </button>
        )}
      </div>

      {sinResponsable && !esTerminado && (
        <div className="tk-responder-cerrado" data-testid="derivar-sin-responsable-hint">
          <Lock style={{ width: 14, height: 14, flexShrink: 0 }} />
          <span>Asigna un <strong>responsable principal</strong> al ticket para poder derivar a gestión interna.</span>
        </div>
      )}

      {esTerminado && (
        <div className="tk-responder-cerrado" data-testid="derivar-terminado-hint">
          <CheckCircle2 style={{ width: 14, height: 14, flexShrink: 0 }} />
          <span>Este ticket está <strong>terminado</strong>. Para derivar a gestión interna, reábralo cambiando el estado a <strong>«En proceso»</strong>.</span>
        </div>
      )}

      {!puedeDerivar && gestiones.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--tk-text-soft)', fontStyle: 'italic' }}>
          Solo el responsable del ticket (o un administrador/supervisor) puede derivar internamente.
        </p>
      )}

      {derivOpen && puedeDerivar && !esTerminado && !sinResponsable && (
        <div className="tk-gi-deriv-form" data-testid="deriv-form">
          <div className="tk-gi-deriv-title">
            <Share2 style={{ width: 14, height: 14, color: 'var(--tk-internal, #5A7A7A)' }} /> Nueva derivación interna
          </div>
          <div className="tk-gi-deriv-field">
            <div className="tk-label">Responsable</div>
            <Select value={derivResp} onValueChange={setDerivResp}>
              <SelectTrigger className="h-9 text-sm" data-testid="deriv-responsable"><SelectValue placeholder="Selecciona un usuario…" /></SelectTrigger>
              <SelectContent>
                {responsablesInternos.length === 0 && <div style={{ padding: '8px 10px', fontSize: 11, color: 'var(--tk-text-soft)' }}>No hay usuarios con área funcional.</div>}
                {responsablesInternos.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.nombre}{r.area_funcional_nombre ? ` · ${r.area_funcional_nombre}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="tk-gi-deriv-field">
            <div className="tk-label">Prioridad interna (opcional)</div>
            <Select value={derivPrioridad || 'default'} onValueChange={(v) => setDerivPrioridad(v === 'default' ? '' : v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="deriv-prioridad"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">— Por defecto —</SelectItem>
                {prioridadesInternas.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {p.color && <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color }} />}{p.nombre}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="tk-gi-deriv-field">
            <div className="tk-label">Mensaje / instrucciones (opcional)</div>
            <Textarea value={derivNota} onChange={(e) => setDerivNota(e.target.value)} rows={3} className="text-sm" placeholder="Contexto o indicaciones para el responsable…" data-testid="deriv-nota" />
          </div>
          <button className="tk-btn-primary-lg tk-gi-deriv-cta" onClick={onDerivar} disabled={derivando || !derivResp} data-testid="btn-confirmar-derivar" type="button">
            {derivando ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Share2 style={{ width: 14, height: 14 }} />}
            Derivar a usuario
          </button>
        </div>
      )}

      <div className="tk-gi-list" data-testid="gi-list">
        {gestiones.length === 0 ? (
          <div className="tk-gi-empty" data-testid="gi-empty">
            <Share2 className="tk-gi-empty-icon" style={{ width: 28, height: 28 }} />
            <div className="tk-gi-empty-title">Sin derivaciones internas</div>
            <div className="tk-gi-empty-sub">Aún no se ha derivado este ticket a ningún usuario para gestión interna.</div>
          </div>
        ) : gestiones.map((g) => (
          <GestionCard
            key={g.id} g={g}
            gestionMsg={gestionMsg} setGestionMsg={setGestionMsg}
            gestionBusy={gestionBusy}
            gestionAdjuntos={gestionAdjuntos} gestionUploading={gestionUploading}
            onSubirAdjunto={onSubirAdjunto} onQuitarAdjunto={onQuitarAdjunto}
            onResponder={onResponder} onCerrar={onCerrar} onResponderYCerrar={onResponderYCerrar} onVerAdjunto={onVerAdjunto}
            user={user}
          />
        ))}
      </div>
    </div>
  );
}
